import { config } from "./config";
import type { PlanId } from "./funnelData";

export type CheckoutOfferId = "polar_funnel_lifetime" | "stripe_lifetime" | "polar_trial_7d";
export type CheckoutProvider = "stripe" | "polar";

export type CheckoutOffer = {
  id: CheckoutOfferId;
  provider: CheckoutProvider;
  analyticsKey: string;
  trialDays: number;
  priceDisplay: string;
  priceAmount: number;
  currency: string;
  renewalCopy: string;
  checkoutUrl: string;
  productId: string;
  planConfig: Partial<Record<PlanId, {
    checkoutUrl: string;
    productId: string;
    renewalDisplay: string;
  }>>;
};

const TRIAL_OFFER_QUERY_VALUES = new Set([
  "trial-yearly",
  "trial_yearly",
  "trial-099-7d",
  "trial_099_7d",
  "trial-7d",
  "trial_7d",
  "free-trial",
  "free_trial",
  "polar-trial",
  "polar_trial",
  "polar_trial_099_7d"
]);

const STRIPE_OFFER_QUERY_VALUES = new Set([
  "stripe",
  "stripe-lifetime",
  "stripe_lifetime"
]);

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

const parsePositiveInteger = (value: string, fallback: number): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const parsePositiveNumber = (value: string, fallback: number): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const defaultCheckoutOffer: CheckoutOffer = {
  id: "polar_funnel_lifetime",
  provider: "polar",
  analyticsKey: "polar_funnel_lifetime",
  trialDays: 0,
  priceDisplay: "",
  priceAmount: 0,
  currency: "USD",
  renewalCopy: "",
  checkoutUrl: "",
  productId: "",
  planConfig: {}
};

export const stripeLifetimeCheckoutOffer: CheckoutOffer = {
  ...defaultCheckoutOffer,
  id: "stripe_lifetime",
  provider: "stripe",
  analyticsKey: "stripe_lifetime"
};

export const getCheckoutOfferFromLocation = (location: Location): CheckoutOffer => {
  const params = new URLSearchParams(location.search);
  const rawOffer = params.get("offer") ?? params.get("checkout_offer") ?? "";
  const normalizedOffer = rawOffer.trim().toLowerCase();

  if (STRIPE_OFFER_QUERY_VALUES.has(normalizedOffer)) {
    return stripeLifetimeCheckoutOffer;
  }

  if (!TRIAL_OFFER_QUERY_VALUES.has(normalizedOffer)) {
    return defaultCheckoutOffer;
  }

  return {
    id: "polar_trial_7d",
    provider: "polar",
    analyticsKey: "polar_trial_7d",
    trialDays: parsePositiveInteger(config.polarTrialDays, 7),
    priceDisplay: config.polarTrialPriceDisplay,
    priceAmount: parsePositiveNumber(config.polarTrialPriceAmount, 0),
    currency: config.polarTrialCurrency.toUpperCase(),
    renewalCopy: config.polarTrialRenewalCopy,
    checkoutUrl: config.polarTrialCheckoutUrl,
    productId: config.polarTrialProductId,
    planConfig: {
      standard: {
        checkoutUrl: config.polarTrialStandardCheckoutUrl || config.polarTrialCheckoutUrl,
        productId: config.polarTrialStandardProductId || config.polarTrialProductId,
        renewalDisplay: config.polarTrialStandardRenewalDisplay
      },
      pro: {
        checkoutUrl: config.polarTrialProCheckoutUrl || config.polarTrialCheckoutUrl,
        productId: config.polarTrialProProductId || config.polarTrialProductId,
        renewalDisplay: config.polarTrialProRenewalDisplay
      }
    }
  };
};

export const isPolarTrialOffer = (offer: CheckoutOffer): boolean => offer.id === "polar_trial_7d";

export const buildPolarCheckoutUrl = (
  offer: CheckoutOffer,
  input: {
    email?: string;
    leadId?: number | null;
    planId: PlanId;
    eventId: string;
    currentLocation: Location;
  }
): string => {
  const planConfig = offer.planConfig[input.planId];
  const planCheckoutUrl = planConfig?.checkoutUrl || offer.checkoutUrl;
  const planProductId = planConfig?.productId || offer.productId;

  if (!planCheckoutUrl) {
    throw new Error(
      `Polar trial checkout URL is not configured for ${input.planId}. Set VITE_POLAR_TRIAL_${input.planId.toUpperCase()}_CHECKOUT_URL for this build.`
    );
  }

  const checkoutUrl = new URL(planCheckoutUrl);
  if (planProductId && !checkoutUrl.searchParams.has("product_id")) {
    checkoutUrl.searchParams.set("product_id", planProductId);
  }
  if (input.email) {
    checkoutUrl.searchParams.set("customer_email", input.email);
  }
  if (!checkoutUrl.searchParams.has("reference_id")) {
    checkoutUrl.searchParams.set(
      "reference_id",
      input.leadId ? `linkhiv-${input.leadId}` : `linkhiv-${input.eventId}`
    );
  }
  if (!checkoutUrl.searchParams.has("theme")) {
    checkoutUrl.searchParams.set("theme", "light");
  }

  const sourceParams = new URLSearchParams(input.currentLocation.search);
  UTM_PARAMS.forEach((param) => {
    const value = sourceParams.get(param);
    if (value && !checkoutUrl.searchParams.has(param)) {
      checkoutUrl.searchParams.set(param, value);
    }
  });

  return checkoutUrl.toString();
};
